import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  StackProps,
  Typography,
} from '@mui/material'

import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { MouseEvent, useState } from 'react'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Theme, useTheme, styled } from '@mui/material/styles'
import { MainLogo, GoogleIcon } from 'components/images'
import { KTextField } from 'components/core/KTextField'
import { KButton } from 'components/core/KButton'
import { Background1 } from 'components/backgrounds/Background1'
import { useSignIn } from 'hooks/AuthHooks'
import { useToast } from 'contexts'

const FormContainer = styled(Stack)<StackProps>((props) => {
  const theme: Theme = props.theme as unknown as Theme
  return {
    margin: 'auto',
    width: 400,
    minWidth: 320,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  }
})

interface LoginParams {
  email: string
  password: string
}

const LoginForm = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const signIn = useSignIn()
  const { showToast } = useToast()

  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginParams>()
  const doSubmit = async (data: LoginParams) => {
    try {
      // For now, we'll just use Google sign-in
      // In the future, you might want to implement username/password login
      await signIn('Google')
    } catch (error) {
      console.error('Error during sign in:', error)
      showToast(t('Failed to sign in. Please try again.'), 'error')
    }
  }

  return (
    <Stack
      component="form"
      spacing={2}
      noValidate
      autoComplete="off"
      sx={{ width: '100%' }}
    >
      <KTextField
        required
        label={t('Email')}
        placeholder="chachapos@gmail.com"
        autoFocus
        inputProps={{ maxLength: 50 }}
        helperText={errors?.email?.message}
        error={!!errors?.email}
        {...register('email', {
          required: t('This field is required'),
          pattern: {
            value: /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/,
            message: t('Invalid email'),
          },
        })}
      />
      <KTextField
        required
        type={showPassword ? 'text' : 'password'}
        label={t('Password')}
        inputProps={{ maxLength: 50 }}
        error={!!errors?.password}
        helperText={errors?.password?.message}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        {...register('password', { required: t('This field is required') })}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="text"
          color="secondary"
          href="#"
          sx={{
            padding: 0,
            textDecoration: 'underline',
            fontSize: theme.typography.body1.fontSize,
          }}
        >
          {t('Forget password')}
        </Button>
      </Box>
      <KButton onClick={handleSubmit(doSubmit)}>
        {/* {isSigningInUP ? <CircularProgress size={40} /> : t('Login')} */}
        {t('Login')}
      </KButton>
      <KButton variant="secondary">{t('Sign up')}</KButton>
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
        {t('or continue with')}
      </Typography>
      <Stack direction="row" spacing={2} justifyContent="center">
        <Box sx={{ cursor: 'pointer' }} onClick={handleSubmit(doSubmit)}>
          <GoogleIcon />
        </Box>
      </Stack>
    </Stack>
  )
}

export const SignInPage = () => {
  const theme = useTheme()
  return (
    <Background1
      sx={{
        height: `calc(100% - ${theme.spacing(8)})`,
        display: 'flex',
        alignItems: 'center',
        overflowY: 'auto',
        paddingY: 4,
      }}
    >
      <FormContainer>
        <MainLogo width="100%" />
        <LoginForm />
      </FormContainer>
    </Background1>
  )
}
